import { FieldHeaderComponent } from '@vivli/shared/components';
import { ButtonView } from '@vivli/shared/views';
import { DataPackageValidationMessageComponent } from '../data-package-validation-message.component';
import { Styles } from '@vivli/shared/theme';
import { DTIAMRCommonConst } from '@vivli/shared/infrastructure/constants';
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useDataPackageContext } from '@vivli/shared/features/data-package/infrastructure/context';
import { useActiveUserContext, useAppConfigContext, useVivliConfigContext } from '@vivli/core/infrastructure/context';
import { DataPackageStatusEnum } from '@vivli/features/studies/infrastructure/enum';
import { IFileExtension } from '@vivli/shared/infrastructure/interface';
import { DataPackageUploaderComponent } from '@vivli/shared/features/data-package/components';

const noFilesStyle: CSSProperties = {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center'
};

const titleContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    columnGap: '10px',
    justifyContent: 'center'
};

const btnContainerStyle: CSSProperties = {
    marginTop: '20px'
};

const containerStyle: CSSProperties = {
    width: '100%',
    display: 'grid',
    gridAutoRows: 'auto',
    rowGap: '10px'
};

const buttonStyleClose: CSSProperties = { ...Styles.Button, height: 40, margin: `20px auto 0`, width: 150 };

const buttonStyle: CSSProperties = { ...Styles.Button.BUTTON_LIGHT_VIVLI_BLUE };

export const DataPackageV2CoreComponent = () => {
    const { user } = useActiveUserContext();
    const _config = useAppConfigContext();
    const _vivliConfig = useVivliConfigContext();

    const dataPackageContext = useDataPackageContext();
    const {
        dataPackage,
        isLoading,
        requiredTypes,
        validationMode,
        allowSubmit,
        hasRequiredTypes,
        hasIncompleteUploads,
        readOnly,
        onPackageSubmit,
        title,
        showChatUploadFileWarning,
        onReady,
        onClose,
        isSubmittingFiles,
        useSecureStorage = true,
        allowUpload,
        hybridMode,
        secondaryFilesToShow
    } = dataPackageContext;

    const initRef = useRef(false);

    const uploadUri = useMemo(() => `${_config.secureApiUri}api/files/${dataPackage.id}/${useSecureStorage}`, [dataPackage.id]);
    const isDraft = useMemo(() => dataPackage.status === DataPackageStatusEnum.Draft, [dataPackage.status]);
    const [requiredTypesForDisplay, setRequiredTypesForDisplay] = useState<string[]>();
    const packageType = useMemo(() => dataPackage?.dataPackageType, [dataPackage]);
    const [currentFileExtensions, setCurrentFileExtensions] = useState<IFileExtension[]>([]);

    const handlePackageSubmit = (e) => {
        e.preventDefault();

        onPackageSubmit && onPackageSubmit(dataPackage);
    };

    useEffect(() => {
        if (!dataPackage) {
            return;
        }

        if (!initRef.current) {
            initRef.current = true;
            onReady && onReady();
        }
    }, [dataPackage]);

    useEffect(() => {
        if (!_vivliConfig) {
            return;
        }
        setCurrentFileExtensions(_vivliConfig?.fileExtensions);

        const req = requiredTypes?.map((type) => {
            const rec = _vivliConfig?.ipdFileTypes?.find((t) => t.fileType === type);
            if (rec) {
                return rec.displayName;
            }
            return type;
        });
        setRequiredTypesForDisplay(req);
    }, [_vivliConfig]);

    if (
        !dataPackage ||
        (dataPackage.dataPackageFileDescriptors?.length === 0 && readOnly && (!secondaryFilesToShow || secondaryFilesToShow.length === 0))
    ) {
        return (
            <div style={noFilesStyle}>
                <FieldHeaderComponent title={'NO FILES IN PACKAGE'} />
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            {/* hasIncompleteUploads is part of the condition, not just hasRequiredTypes: a failed file on
                the default 'Unknown' type leaves every required type satisfied, so without it the banner
                would not render at all and Submit would be disabled with nothing on screen explaining
                why — the exact problem DEV-699 set out to remove. (DEV-746) */}
            {allowUpload && allowSubmit && (!hasRequiredTypes || hasIncompleteUploads) && isDraft && (
                <DataPackageValidationMessageComponent
                    requiredTypes={requiredTypes}
                    requiredTypesForDisplay={requiredTypesForDisplay}
                    files={dataPackage.dataPackageFileDescriptors}
                    validationMode={validationMode}
                />
            )}

            <div style={titleContainerStyle}>{(title && <FieldHeaderComponent title={title} />) || <div />}</div>

            <DataPackageUploaderComponent
                uploadUri={uploadUri}
                user={user}
                isLoading={isLoading || isSubmittingFiles}
                fileExtensions={currentFileExtensions}
                {...dataPackageContext}
                packageType={packageType}
                readonly={readOnly}
                hybridMode={hybridMode}
            />

            <div style={btnContainerStyle}>
                {allowSubmit && isDraft && dataPackage?.dataPackageFileDescriptors?.length > 0 && (
                    <ButtonView
                        onClick={handlePackageSubmit}
                        style={buttonStyle}
                        // hasIncompleteUploads blocks a package that still holds a failed or in-flight
                        // file of ANY type. hasRequiredTypes only covers the required types, so it let
                        // an 'Unknown'-type failure through to a server-side rejection. (DEV-746)
                        disabled={
                            isSubmittingFiles ||
                            isLoading ||
                            !hasRequiredTypes ||
                            hasIncompleteUploads ||
                            dataPackage.dataPackageFileDescriptors?.length === 0
                        }
                        className="dataPackage_submit"
                        dataId={DTIAMRCommonConst.SubmitButton}
                    >
                        Submit Files
                    </ButtonView>
                )}

                {onClose && (
                    <ButtonView dataId={DTIAMRCommonConst.OkButton} onClick={onClose} style={buttonStyleClose}>
                        Ok
                    </ButtonView>
                )}
            </div>
        </div>
    );
};
